import * as THREE from "three"
import Experience from "./Experience"
import { OrbitControls } from "three/examples/jsm/Addons.js"
import { gsap } from "gsap"

export default class Camera {

    constructor() {
        this.experience = new Experience()
        this.sizes = this.experience.sizes
        this.scene = this.experience.scene
        this.canvas = this.experience.canvas
        this.debug = this.experience.debug

        // 尝试获取 display 实例（可能在构造时还未创建）
        this.display = this.experience.world?.display
        // console.log('Display instance:', this.display);
        
        // 鼠标/触摸位置 (归一化到 -1 到 1)
        this.mouse = new THREE.Vector2(0, 0)
        this.isTouching = false

        // 镜头状态
        this.cameraState = {
            isLookingAtMonitor: false,
            isMouseOverMonitor: false,
            transitionSpeed: 0.02,
            isTransitioning: false
        }

        // 镜头偏移配置
        this.offsetConfig = {
            intensity: this.isMobile() ? 0.5 : 1.0,
            smoothness: this.isMobile() ? 0.03 : 0.02,
            maxOffset: this.isMobile() ? 1.5 : 2.0
        };

        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder('Camera')
        }

        // 基础摄像机位置和目标
        this.basePosition = new THREE.Vector3(-7, 5, -2.5)
        this.baseFocus = new THREE.Vector3(-3, 3.5, -3)

        // Monitor position
        this.monitorPosition = new THREE.Vector3(-1.6, 3.5, -2.1)
        this.monitorFocusPosition = new THREE.Vector3(-1.6, 3.5, -2.1)
        this.monitorCameraPosition = new THREE.Vector3(-4, 3.5, -2)

        // 当前目标位置 (用于平滑过渡)
        this.targetPos = this.basePosition.clone()
        this.targetFoc = this.baseFocus.clone()

        // GSAP 动画对象
        this.cameraAnimation = null
        this.focusAnimation = null

        this.setInstance()


        // 等待 display 准备就绪后再设置输入跟踪和陀螺仪
        if (this.display) {
            this.display.on('ready', () => {
                // console.log('Display is ready, setting up camera input tracking and device orientation handling');

                // now controled by Experience.js
                this.setupInputTracking()
                this.display = this.experience.world.display
                
                // 如果是移动端，设置陀螺仪控制
                if (this.isMobile()) {
                    this.setupDeviceOrientation().catch((error) => {
                        console.error('Failed to setup device orientation:', error);
                    });
                }
            });
        } else {
            // 如果 display 还不存在，在下一帧再试
            setTimeout(() => {
                if (this.experience.world && this.experience.world.display) {
                    this.display = this.experience.world.display;
                    this.display.on('ready', () => {
                        this.setupInputTracking();
                        if (this.isMobile()) {
                            this.setupDeviceOrientation().catch((error) => {
                                console.error('Failed to setup device orientation:', error);
                            });
                        }
                    });
                }
            }, 100);
        }

        

        
    }

    isMobile() {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
            ('ontouchstart' in window) ||
            (navigator.maxTouchPoints > 0);
    }

    setInstance() {
        this.instance = new THREE.PerspectiveCamera(35, this.sizes.width / this.sizes.height, 0.1, 20)
        this.instance.position.copy(this.basePosition)
        this.instance.lookAt(this.baseFocus)

        if (this.debug.active) {
            this.debugFolder.add(this.instance.position, 'x').min(-10).max(10).step(0.01).name('Camera Position X')
            this.debugFolder.add(this.instance.position, 'y').min(-10).max(10).step(0.01).name('Camera Position Y')
            this.debugFolder.add(this.instance.position, 'z').min(-10).max(10).step(0.01).name('Camera Position Z')

            this.debugFolder.add(this.offsetConfig, 'intensity', 0, 5, 0.1).name('Offset Intensity')
            this.debugFolder.add(this.offsetConfig, 'smoothness', 0.001, 0.1, 0.001).name('Smoothness')
            this.debugFolder.add(this.offsetConfig, 'maxOffset', 0, 10, 0.1).name('Max Offset')

            const monitorFolder = this.debugFolder.addFolder('Monitor Camera')
            monitorFolder.add(this.monitorCameraPosition, 'x').min(-5).max(5).step(0.01).name('Monitor Cam X')
            monitorFolder.add(this.monitorCameraPosition, 'y').min(0).max(10).step(0.01).name('Monitor Cam Y')
            monitorFolder.add(this.monitorCameraPosition, 'z').min(-5).max(5).step(0.01).name('Monitor Cam Z')

            const controls = {
                lookAtMonitor: () => this.switchToMonitorView(),
                returnToBase: () => this.switchToBaseView()
            }
            this.debugFolder.add(controls, 'lookAtMonitor').name('Look at Monitor')
            this.debugFolder.add(controls, 'returnToBase').name('Return to Base')
        }

        this.scene.add(this.instance)
    }

    setupInputTracking() {

        const display = document.querySelector('#display-container');



        // 鼠标事件（桌面端）
        if (!this.isMobile()) {
            window.addEventListener('mousemove', (event) => {
                this.updateInputPosition(event.clientX, event.clientY);
                
            });

                
    
            
            display.addEventListener('mouseenter', (event) => {
                
                this.cameraState.isMouseOverMonitor = true;
                this.cameraState.isLookingAtMonitor = true;
                // 1 秒之后接收鼠标时间（镜头过度动画）
                setTimeout(() => {
                    this.display.iframe.style.pointerEvents = 'auto'; // 允许 iframe 接收鼠标事件
                }, 1000);
                this.freezeCameraPosition();
                this.switchToMonitorView();
            })
            display.addEventListener('mouseleave', (event) => {
                this.cameraState.isMouseOverMonitor = false;
                this.cameraState.isLookingAtMonitor = false;
                this.display.iframe.style.pointerEvents = 'none'; // 禁止 iframe 接收鼠标事件
                // console.log('Mouse left monitor, switching to base view');
                this.unfreezeCameraPosition();
                this.switchToBaseView();
            })
            // 保留点击事件，但用于其他交互
            // window.addEventListener('click', (event) => {
            //     this.handleClick(event);
            // });
        }else{
            // 移动端处理touchmove事件以更新输入位置
            window.addEventListener('touchmove', (event) => {
                
                if ( event.touches.length > 0) {
                    this.updateInputPosition(event.touches[0].clientX, event.touches[0].clientY);
                }
            }, { passive: true });

            display.addEventListener('touchstart', (event) => {
                this.cameraState.isMouseOverMonitor = true;
                this.cameraState.isLookingAtMonitor = true;
                // 1 秒之后接收鼠标时间（镜头过度动画）
                setTimeout(() => {
                    this.display.iframe.style.pointerEvents = 'auto'; // 允许 iframe 接收鼠标事件
                }, 1000);
                this.freezeCameraPosition();
                this.switchToMonitorView();
                
                // 添加返回按钮或手势逻辑以返回基础视角
                
            })
            //点击display 外区域返回基础视角
            window.addEventListener('touchstart', (event) => {
                const target = event.target;
                if (this.cameraState.isMouseOverMonitor && target !== display && !display.contains(target)) {
                    this.cameraState.isMouseOverMonitor = false;
                    this.cameraState.isLookingAtMonitor = false;
                    setTimeout(() => {
                        this.display.iframe.style.pointerEvents = 'none'; // 禁止 iframe 接收鼠标事件
                    }, 500);
                    this.unfreezeCameraPosition();
                    this.switchToBaseView();
                }
            });
            
        }


    }

    // 检测鼠标是否悬停在监视器上
    checkMonitorHover(event) {
        const mouse = new THREE.Vector2();
        mouse.x = (event.clientX / this.sizes.width) * 2 - 1;
        mouse.y = -(event.clientY / this.sizes.height) * 2 + 1;

        const target = event.target

        // 检查鼠标是否在监视器 iframe 上
        if (target && target.tagName === 'IFRAME') {
            // 如果鼠标在 iframe 上，直接返回
            this.cameraState.isMouseOverMonitor = true;
            this.cameraState.isLookingAtMonitor = true;
            this.switchToMonitorView();
            

        }
    }

    // 冻结镜头位置（鼠标在监视器上时）
    freezeCameraPosition() {
        // console.log('Freezing camera position - mouse over monitor');
        // 可以在这里添加额外的逻辑，比如显示UI等
    }

    // 解冻镜头位置（鼠标离开监视器时）
    unfreezeCameraPosition() {
        // console.log('Unfreezing camera position - mouse left monitor');
        // 可以在这里添加额外的逻辑
    }


    // 使用 GSAP 切换到监视器视角
    switchToMonitorView() {
        if (this.cameraState.isTransitioning) return;

        this.cameraState.isLookingAtMonitor = true;
        this.cameraState.isTransitioning = true;
        // console.log('Switching to monitor view with GSAP');

        // 停止当前动画
        if (this.cameraAnimation) this.cameraAnimation.kill();
        if (this.focusAnimation) this.focusAnimation.kill();

        // 动画到监视器位置
        this.cameraAnimation = gsap.to(this.targetPos, {
            duration: 1,
            x: this.monitorCameraPosition.x,
            y: this.monitorCameraPosition.y,
            z: this.monitorCameraPosition.z,
            ease: "power2.inOut",
            onComplete: () => {
                this.cameraState.isTransitioning = false;
            }
        });

        this.focusAnimation = gsap.to(this.targetFoc, {
            duration: 1,
            x: this.monitorFocusPosition.x,
            y: this.monitorFocusPosition.y,
            z: this.monitorFocusPosition.z,
            ease: "power2.inOut"
        });
    }

    // 使用 GSAP 切换回基础视角
    switchToBaseView() {
        if (this.cameraState.isTransitioning) return;

        this.cameraState.isLookingAtMonitor = false;
        this.cameraState.isTransitioning = true;
        

        // 停止当前动画
        if (this.cameraAnimation) this.cameraAnimation.kill();
        if (this.focusAnimation) this.focusAnimation.kill();

        // 动画回到基础位置
        this.cameraAnimation = gsap.to(this.targetPos, {
            duration: 1,
            x: this.basePosition.x,
            y: this.basePosition.y,
            z: this.basePosition.z,
            ease: "power2.inOut",
            onComplete: () => {
                this.cameraState.isTransitioning = false;
            }
        });

        this.focusAnimation = gsap.to(this.targetFoc, {
            duration: 1,
            x: this.baseFocus.x,
            y: this.baseFocus.y,
            z: this.baseFocus.z,
            ease: "power2.inOut"
        });
    }

    updateInputPosition(clientX, clientY) {
        this.mouse.x = (clientX / this.sizes.width) * 2 - 1;
        this.mouse.y = -((clientY / this.sizes.height) * 2 - 1);
    }

    async setupDeviceOrientation() {
        // 请求设备方向权限（iOS 13+）
        if (typeof DeviceOrientationEvent !== 'undefined' && typeof DeviceOrientationEvent.requestPermission === 'function') {
            try {
                const permission = await DeviceOrientationEvent.requestPermission();
                if (permission !== 'granted') {
                    console.warn('Device orientation permission denied. Gyroscope controls will not work.');
                    // 权限被拒绝时，可以使用触摸控制作为后备
                    return;
                }
            } catch (error) {
                console.error('Error requesting device orientation permission:', error);
                return;
            }
        }

        // 检查是否支持设备方向事件
        if (typeof DeviceOrientationEvent === 'undefined') {
            console.warn('DeviceOrientationEvent is not supported on this device');
            return;
        }

        // 陀螺仪状态
        this.gyroEnabled = true;
        this.gyroCalibration = {
            alpha: 0,
            beta: 0,
            gamma: 0
        };
        this.gyroInitialized = false;
        this.calibrationSamples = 0;
        const CALIBRATION_SAMPLE_COUNT = 10;

        // 初始化时校准（设置初始值作为基准）
        const handleOrientation = (event) => {
            if (!this.gyroEnabled || this.cameraState.isTransitioning || this.cameraState.isLookingAtMonitor) {
                return;
            }

            const { alpha, beta, gamma } = event;

            // 检查数据是否有效
            if (alpha === null || beta === null || gamma === null) {
                return;
            }

            // 校准阶段：收集初始数据作为基准
            if (!this.gyroInitialized && this.calibrationSamples < CALIBRATION_SAMPLE_COUNT) {
                this.gyroCalibration.alpha += alpha;
                this.gyroCalibration.beta += beta;
                this.gyroCalibration.gamma += gamma;
                this.calibrationSamples++;
                
                if (this.calibrationSamples >= CALIBRATION_SAMPLE_COUNT) {
                    this.gyroCalibration.alpha /= CALIBRATION_SAMPLE_COUNT;
                    this.gyroCalibration.beta /= CALIBRATION_SAMPLE_COUNT;
                    this.gyroCalibration.gamma /= CALIBRATION_SAMPLE_COUNT;
                    this.gyroInitialized = true;
                    console.log('Gyroscope calibrated');
                }
                return;
            }

            if (!this.gyroInitialized) {
                return;
            }

            // 计算相对于校准基准的偏移量
            const deltaGamma = gamma - this.gyroCalibration.gamma;
            const deltaBeta = beta - this.gyroCalibration.beta;

            // 将设备方向映射到鼠标位置（归一化到 -1 到 1）
            // gamma: 左右倾斜（-90 到 90），映射到 mouse.x（相机左右移动）
            // beta: 前后倾斜（0 到 180），映射到 mouse.y（相机上下移动）
            // 使用相对偏移量，确保从校准点开始计算
            const sensitivity = 0.02; // 灵敏度系数，控制响应程度（越小越平滑）
            const rawX = deltaGamma * sensitivity;
            const rawY = deltaBeta * sensitivity;

            // 限制在 -1 到 1 范围内（这将在 update 方法中进一步被 maxOffset 限制）
            this.mouse.x = THREE.MathUtils.clamp(rawX, -1, 1);
            this.mouse.y = THREE.MathUtils.clamp(rawY, -1, 1);
        };

        // 监听设备方向变化
        if (typeof DeviceOrientationEvent !== 'undefined') {
            window.addEventListener('deviceorientation', handleOrientation, false);
        } else {
            console.warn('DeviceOrientationEvent is not supported');
        }

        // 调试面板控制
        if (this.debug.active) {
            const gyroControls = { 
                enabled: true,
                recalibrate: () => {
                    this.gyroInitialized = false;
                    this.calibrationSamples = 0;
                    this.gyroCalibration = { alpha: 0, beta: 0, gamma: 0 };
                    this.mouse.set(0, 0);
                }
            };
            this.debugFolder.add(gyroControls, 'enabled').name('Gyroscope Enabled').onChange((value) => {
                this.gyroEnabled = value;
                if (!value) {
                    this.mouse.set(0, 0);
                }
            });
            this.debugFolder.add(gyroControls, 'recalibrate').name('Recalibrate Gyro');
        }
    }

    returnToCenter() {
        gsap.to(this.mouse, {
            duration: 0.5,
            x: 0,
            y: 0,
            ease: "power2.out"
        });
    }

    setOrbitControls() {
        this.controls = new OrbitControls(this.instance, this.canvas)
        this.controls.enableDamping = true
        this.controls.enableZoom = !this.isMobile()

        if (this.isMobile()) {
            this.controls.enablePan = false;
            this.controls.touches = {
                ONE: THREE.TOUCH.ROTATE,
                TWO: THREE.TOUCH.DOLLY_PAN
            };
        }

        this.controls.update()
    }

    resize() {
        this.instance.aspect = this.sizes.width / this.sizes.height
        this.instance.updateProjectionMatrix()

        if (this.isMobile()) {
            this.offsetConfig.intensity = this.sizes.width > this.sizes.height ? 0.6 : 0.4;
        }
    }

    update() {



            // 如果正在过渡中，不计算鼠标偏移
            if (this.cameraState.isTransitioning) {
                this.instance.position.copy(this.targetPos);
                this.instance.lookAt(this.targetFoc);
                return;
            }

            // 如果在监视器模式且鼠标在监视器上，停止更新镜头位置
            if (this.cameraState.isLookingAtMonitor) {
                this.instance.position.copy(this.targetPos);
                this.instance.lookAt(this.targetFoc);
                return;
            }

            // 根据当前状态决定基础位置和焦点
            let basePos, baseFoc;

            if (this.cameraState.isLookingAtMonitor) {
                basePos = this.monitorCameraPosition;
                baseFoc = this.monitorFocusPosition;
            } else {
                basePos = this.basePosition;
                baseFoc = this.baseFocus;
            }

            // 计算基于输入位置的偏移
            const offsetMultiplier = this.cameraState.isLookingAtMonitor ? 0.2 : 1.0;
            const offsetX = this.mouse.x * this.offsetConfig.intensity * offsetMultiplier;
            const offsetY = this.mouse.y * this.offsetConfig.intensity * offsetMultiplier;

            // 计算目标位置
            const targetCameraPos = new THREE.Vector3(
                basePos.x + offsetY * 0.3,
                basePos.y + offsetY * 0.5,
                basePos.z + offsetX
            );

            // 计算目标焦点
            const targetFocusPos = new THREE.Vector3(
                baseFoc.x + offsetY * 0.2,
                baseFoc.y + offsetY * 0.6,
                baseFoc.z + offsetX * (this.cameraState.isLookingAtMonitor ? 0.1 : 1.5)
            );

            // 限制偏移范围
            const maxOffset = this.cameraState.isLookingAtMonitor ?
                this.offsetConfig.maxOffset * 0.3 : this.offsetConfig.maxOffset;

            targetCameraPos.z = THREE.MathUtils.clamp(
                targetCameraPos.z,
                basePos.z - maxOffset,
                basePos.z + maxOffset
            );
            targetCameraPos.y = THREE.MathUtils.clamp(
                targetCameraPos.y,
                basePos.y - maxOffset,
                basePos.y + maxOffset
            );

            // 平滑过渡到目标位置
            this.targetPos.lerp(targetCameraPos, this.offsetConfig.smoothness);
            this.targetFoc.lerp(targetFocusPos, this.offsetConfig.smoothness);

            // 应用到摄像机
            this.instance.position.copy(this.targetPos);
            this.instance.lookAt(this.targetFoc);
        }



        dispose() {
            // 清理 GSAP 动画
            if (this.cameraAnimation) this.cameraAnimation.kill();
            if (this.focusAnimation) this.focusAnimation.kill();
        }

        
    
    }